/**
 * paymentStatus.ts
 * Square決済DB (DS1) のデータからバッジ表示用のステータスを計算するユーティリティ
 */

export interface SquarePaymentEntry {
  invoices: any[];
  payments: any[];
  orders: any[];
  refunds: any[];
  fetchedAt: number;
}

export interface PaymentStatus {
  /** バッジテキスト: 'SQ済' | '済' | '部分' | '未払' | '' */
  text: string;
  /** バッジ色 (hex) */
  color: string;
  /** 支払済み合計 (円) */
  totalPaidAmount: number;
  /** 返金があるか */
  hasRefunds: boolean;
  /** 返金合計 (円) */
  refundAmount: number;
}

/**
 * DS1のキャッシュエントリからバッジ表示用のステータスを計算する
 *
 * @param entry     DS1キャッシュエントリ (undefined = データなし)
 * @param isManualPaid  detail.payment === 1 (手動確認済みフラグ)
 * @param isLoading データ取得中かどうか
 */
export function computePaymentStatus(
  entry: SquarePaymentEntry | undefined,
  isManualPaid: boolean,
  isLoading: boolean,
): PaymentStatus {
  const empty: PaymentStatus = {
    text: '',
    color: '#757575',
    totalPaidAmount: 0,
    hasRefunds: false,
    refundAmount: 0,
  };

  // Loading
  if (isLoading) {
    return { ...empty, text: '...' };
  }

  // 確認済みフラグ かつ キャッシュなし → 「済」
  if (isManualPaid && !entry) {
    return { ...empty, text: '済', color: '#4caf50' };
  }

  // データなし
  if (!entry) {
    return empty;
  }

  const { invoices, payments, refunds } = entry;

  // Invoice がない場合
  if (!invoices || invoices.length === 0) {
    if (isManualPaid) {
      return { ...empty, text: '済', color: '#4caf50' };
    }
    return empty;
  }

  // --- 支払済み金額の計算 ---
  // DS1 payments テーブル: total_amount はセント単位
  const totalPaidAmount = payments
    .filter((p: any) => p.status === 'COMPLETED')
    .reduce((sum: number, p: any) => {
      const amount = Number(p.total_amount) || 0;
      return sum + Math.round(amount / 100);
    }, 0);

  // --- 返金の計算 ---
  const hasRefunds = refunds && refunds.length > 0;
  const refundAmount = hasRefunds
    ? refunds.reduce((sum: number, r: any) => {
        const amount = Number(r.amount) || 0;
        return sum + Math.round(amount / 100);
      }, 0)
    : 0;

  // --- Invoice ステータスの集計 ---
  // DS1: invoice.status (not invoice_status)
  const hasPaid = invoices.some((inv: any) => inv.status === 'PAID');
  const hasUnpaid = invoices.some(
    (inv: any) => inv.status === 'UNPAID' || inv.status === 'DRAFT',
  );
  const hasPartialPaid = invoices.some(
    (inv: any) => inv.status === 'PARTIALLY_PAID',
  );

  // --- バッジ判定 (優先順位順) ---
  let text = '';
  let color = '#757575';

  if (hasPaid && !hasUnpaid && !hasPartialPaid) {
    text = 'SQ済';
    color = '#4caf50'; // green
  } else if (isManualPaid) {
    text = '済';
    color = '#4caf50'; // green
  } else if (hasPartialPaid) {
    text = '部分';
    color = '#2196f3'; // blue
  } else if (hasUnpaid) {
    text = '未払';
    color = '#ff9800'; // orange
  }

  return {
    text,
    color,
    totalPaidAmount,
    hasRefunds: !!hasRefunds,
    refundAmount,
  };
}
